import { api } from "@/lib/apiClient";
import {
  NEW_DESIGN_CHANGE_EVENT,
  applyNewDesignLocally,
  clearNewDesignPending,
  isNewDesignEnabled,
  markNewDesignPending,
  readNewDesignPending,
} from "@/lib/designPreview";

export async function fetchDesignPreferenceFromServer(): Promise<{
  newDesign: boolean;
  hasPreference: boolean;
}> {
  const pending = readNewDesignPending();
  if (pending !== null) {
    const synced = await persistDesignPreferenceToServer(pending);
    return { newDesign: synced, hasPreference: true };
  }

  const data = await api.get<{ new_design?: unknown; has_preference?: unknown }>(
    "/me/design-preference",
  );
  return {
    newDesign: data?.new_design === true,
    hasPreference: data?.has_preference === true,
  };
}

export async function persistDesignPreferenceToServer(newDesign: boolean): Promise<boolean> {
  markNewDesignPending(newDesign);
  applyNewDesignLocally(newDesign);
  try {
    const data = await api.put<{ new_design?: unknown }>("/me/design-preference", {
      new_design: newDesign,
    });
    const saved = data?.new_design === true ? true : newDesign;
    applyNewDesignLocally(saved);
    clearNewDesignPending();
    return saved;
  } catch {
    return false;
  }
}

export async function saveDesignPreference(next: boolean): Promise<void> {
  await persistDesignPreferenceToServer(next);
}

export async function bootstrapDesignPreferenceFromServer(): Promise<boolean> {
  try {
    const pending = readNewDesignPending();
    if (pending !== null) {
      const synced = await persistDesignPreferenceToServer(pending);
      return synced ? pending : readDesignPreferenceLocal();
    }

    const { newDesign, hasPreference } = await fetchDesignPreferenceFromServer();
    if (!hasPreference) {
      // Keine Server-Präferenz: Normal-App (React) als Standard speichern.
      const saved = await persistDesignPreferenceToServer(true);
      return saved ? true : readDesignPreferenceLocal();
    }
    applyNewDesignLocally(newDesign);
    return newDesign;
  } catch {
    const pending = readNewDesignPending();
    if (pending !== null) {
      applyNewDesignLocally(pending);
      return pending;
    }
    return readDesignPreferenceLocal();
  }
}

/** Für Provider: aktuellen Stand ohne Server-Roundtrip. */
export function readDesignPreferenceLocal(): boolean {
  return isNewDesignEnabled();
}

export { NEW_DESIGN_CHANGE_EVENT };
